import { Button } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router'
import { EnumTipoFacturacion } from '../../../api'
import { InvoiceCycleProcessingModel } from '../../../api/Argentia/endpoints/invoiceCyclesExecutions/model/cfExecutionModel'
import { EnumEstadoCiclo } from '../../../api/oData/enums/enumEstadoCiclo'
import Page, { PageLayout } from '../../../components/page/page'
import PageCenterColumn from '../../../components/page/pageCenterColumn'
import PageHeader from '../../../components/page/pageHeader'
import { NonAuthRoutes } from '../../../model'
import { ServiceResponse } from '../../../model/serviceResponse'
import { addInvoiceCycleProcess } from '../../../redux/actions/processing.actions'
import { useInvoiceCyclePolling } from '../hooks/useInvoiceCyclePolling'
import { fetchInvoiceCycleExecution, fetchInvoiceCycleProcessing } from '../invoiceCycles.service'
import { InvoiceCycleProcessingProgress } from './components/invoiceCycleProcessingProgress'
import { invoiceCycleStatusConfig } from './invoiceCycleStatusConfig'

interface InvoiceCyclesPageProps {
  location?: {
    state?: {
      executionCycleId?: number
      invoiceCycleId?: number
      originState?: EnumEstadoCiclo
      [key: string]: any
    }
  }
}

export const InvoiceCyclesProcessingPage: React.FC<InvoiceCyclesPageProps> = props => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'invoiceCyclesProcessingPage'])
  const history = useHistory()
  const dispatch = useDispatch()

  const executionCycleId = props.location?.state?.executionCycleId
  const cycleName = props.location?.state?.cycleName
  const originState = props.location?.state?.originState
  const additionalState = props.location?.state

  const [currentStatus, setCurrentStatus] = useState<EnumEstadoCiclo>(EnumEstadoCiclo.Procesando)
  const [processingProgress, setProcessingProgress] = useState<InvoiceCycleProcessingModel>({
    processedElements: 0,
    totalProcessingElements: 0
  })
  const [isFetching, setIsFetching] = useState<boolean>(false)

  const isRefacturacionOAnuladora =
    additionalState?.billingType === EnumTipoFacturacion.Anuladora ||
    additionalState?.billingType === EnumTipoFacturacion.Refacturacion

  // #region Handlers Starts

  /**
   * Función que consulta el estado actual del ciclo de ejecución y el progreso en paralelo
   */
  const fetchCycleStatus = async (): Promise<EnumEstadoCiclo> => {
    if (!executionCycleId) {
      console.error('No executionCycleId provided')
      return currentStatus
    }

    try {
      // Ejecutar llamada de ejecución
      const executionResponse = await fetchInvoiceCycleExecution(executionCycleId)

      // Ejecutar llamada de progreso solo si no es Refacturación/Anuladora
      let progressResponse: ServiceResponse<InvoiceCycleProcessingModel | undefined> | undefined
      if (!isRefacturacionOAnuladora) {
        progressResponse = await fetchInvoiceCycleProcessing(executionCycleId)
      }

      // Actualizar estado del ciclo
      if (executionResponse.success && executionResponse.data) {
        const newStatus = executionResponse.data.status as EnumEstadoCiclo

        // No actualizar currentStatus si el BE aún está en el estado de origen
        // (el proceso todavía no arrancó — mantener Procesando en la UI)
        if (!originState || newStatus !== originState) {
          setCurrentStatus(newStatus)
        }

        // Actualizar progreso si está disponible
        if (progressResponse?.success && progressResponse?.data) {
          setProcessingProgress(progressResponse.data)
        }

        return newStatus
      }
      return currentStatus
    } catch (error) {
      console.error('Error fetching cycle status:', error)
      throw error
    }
  }

  const { pollingState, resumePolling, cancelPolling } = useInvoiceCyclePolling(
    executionCycleId!,
    currentStatus,
    fetchCycleStatus,
    additionalState,
    originState
    // Sin disableAutoNavigation - navega automáticamente cuando termina
  )

  const statusConfig = invoiceCycleStatusConfig[currentStatus]

  // ------------------- Header Action Buttons Handlers -----------------------------//

  const handleCancelButtonClick = () => {
    cancelPolling()
  }

  const handleBackAndContinueButtonClick = () => {
    // Mover el proceso a Redux para continuar en segundo plano
    if (executionCycleId) {
      dispatch(addInvoiceCycleProcess(executionCycleId, currentStatus, additionalState, cycleName))
      // Detener el polling local porque ahora se manejará en la notificación flotante
      cancelPolling()
    }
    // Navegar de vuelta al listado
    history.push(NonAuthRoutes.invoiceCyclesNew)
  }

  const handleBackButtonClick = () => {
    // Navegar de vuelta al listado
    history.push(NonAuthRoutes.invoiceCyclesNew)
  }
  // #endregion Handlers Ends

  // #region Effects

  useEffect(() => {
    // Validar que tenemos el ID necesario
    if (!executionCycleId) {
      console.error('No execution cycle ID provided, redirecting to cycles list')
      history.push(NonAuthRoutes.invoiceCyclesNew)
    }
  }, [executionCycleId])

  // #endregion Effects

  return (
    <Page layout={PageLayout.MULTI_COLUMN} fullWidth={true}>
      <PageCenterColumn>
        <PageHeader
          title={t(statusConfig.i18nKey)}
          subtitle={t('invoiceCyclesProcessingPage:header.subtitle')}
        >
          <Button onClick={handleCancelButtonClick} variant="text" color="primary" size="small">
            {t('invoiceCyclesProcessingPage:buttons.cancel')}
          </Button>
          <Button
            onClick={handleBackAndContinueButtonClick}
            variant="text"
            color="primary"
            size="small"
          >
            {t('invoiceCyclesProcessingPage:buttons.backAndContinue')}
          </Button>
          <Button onClick={handleBackButtonClick} variant="text" color="primary" size="small">
            {t('invoiceCyclesProcessingPage:buttons.back')}
          </Button>
        </PageHeader>

        {/* MAIN BODY COMPONENTS */}
        <InvoiceCycleProcessingProgress
          isProcessing={statusConfig.isProcessing}
          pollingState={pollingState}
          onResume={resumePolling}
          onBack={handleBackButtonClick}
          processedElements={processingProgress.processedElements}
          totalProcessingElements={processingProgress.totalProcessingElements}
        />
      </PageCenterColumn>
    </Page>
  )
}
